<template lang="pug">
  #main-map
    div.map-wrap
      .overlay-wrap(v-if='!hasActiveMarkers && !isLoadingResources')
        .overlay
          h1 No resources found
      .overlay-wrap(v-if='!hasActiveMarkers && isLoadingResources')
        .overlay
          h1 Loading...

      gmap-map(
        ref='map'
        :zoom='zoom'
        :center='center'
        :options='options')

        gmap-info-window(
          :options='info.options'
          :position='info.position'
          :opened='info.isOpened'
          @closeclick='info.isOpened = false')

          p(v-if='info.content.title')
            strong {{info.content.title}}
            a(@click='toggleStreetView(info.position)')  (Toggle Street View)
          p(v-if='info.content.mapURL')
            a(:href='info.content.mapURL') {{info.content.address}}
          p(v-if='info.content.website')
            a(:href='info.content.website') {{info.content.website}}
          pre(v-if='info.content.notes') {{info.content.notes}}

        gmap-marker(
          v-for='(resource, key) in visibleResources'
          v-if='resource.lat || resource.lng'
          :key='key'
          :icon='resource.icon'
          :position='{lat: resource.lat, lng: resource.lng}'
          @click='showInfo(resource, key)')
</template>





<script>
  import Vue from 'vue'
  import * as VueGoogleMaps from 'vue2-google-maps'
  import {mapState} from 'vuex'
  import styles from './themes/ultra-light.json'
  import qs from 'qs'

  Vue.use(VueGoogleMaps, {
    load: {
      key: 'AIzaSyD3BUYczjmcnPnJlFCtOKkvH4Y7f_9168Q',
      libraries: 'places'
    }
  })

  export default {
    name: 'map-main',

    props: ['embedded'],

    created () {
      this.$bus.$on('focusMarker', this.centerMap)
      this.$bus.$on('toggleStreetView', this.toggleStreetView)
      this.$store.commit('filterResources')

      if (this.$props.embedded) {
        this.$store.commit('showAllCategories')
      }
    },

    computed: mapState([
      'activeCategories',
      'isLoadingResources',
      'hasActiveMarkers',
      'visibleResources'
    ]),

    mounted () {
      let query = qs.parse(location.search, {ignoreQueryPrefix: true}) || {}

      // Toggle categories based on the querystring
      if (query.cat) {
        this.$store.commit('hideAllCategories')
        this.$store.commit('toggleCategories', query.cat.split(','))
      }

      // Update map query strings
      this.$refs.map.$mapCreated.then(() => {
        this.$refs.map.$mapObject.addListener('dragend', this.rememberMapPosition)
        this.$refs.map.$mapObject.addListener('zoom_changed', this.rememberMapPosition)
      })
    },

    data () {
      // Set the query defaults
      let query = Object.assign({}, {
        lat: 45.5231,
        lng: -122.6765,
        zoom: 15
      }, qs.parse(location.search, {ignoreQueryPrefix: true}))
      query.lat = parseFloat(query.lat)
      query.lng = parseFloat(query.lng)
      query.zoom = parseInt(query.zoom)

      return {
        /**
         * Initializes the Google Map instance with *any* standard setting,
         * even those not documented in vue-google-maps
         *
         * For options: @see https://developers.google.com/maps/documentation/javascript/tutorial#MapOptions
         *
         * For styles: @see https://snazzymaps.com/
         * - Copy/Paste the file into:
         *    /src/components/map/themes/${filename}.json
         *  , include it and pass it here under the `styles` key
         */
        options: {
          styles
        },

        // The maps center
        center: {
          lat: query.lat,
          lng: query.lng
        },

        // The maps zoom
        zoom: query.zoom,

        info: {
          options: {
            // These are part of the vue-google-maps and actually represent left, top
            pixelOffset: {
              width: 0,
              height: -35
            }
          },

          // The markers lat/lng
          position: {
            lat: 0,
            lng: 0
          },

          // The street view's panorama ID
          pano: null,

          // The street view's POV pitch, rotation
          pov: null,

          isOpened: false,

          content: {
            title: '',
            address: '',
            notes: ''
          },

          // The key representing the corresponding marker,
          // which itself represents the Firestore ref ID within the streets collection
          key: null
        }
      }
    },

    methods: {
      /**
       * Shows the infotip over the marker with `key`
       *
       * @param  {OBJ} resource  The resource firestore object
       * @param  {STR} key       The resource key (ref)
       * @param  {BOL} forceShow Whether to force show (true) or to toggle based on this.info.isOpened.
       *                         If true, also scrolls the listing view to the matching listing with `key`
       */
      showInfo (resource, key, forceShow) {
        this.info.position = {
          lat: resource.lat,
          lng: resource.lng,
          pano: resource.pano,
          pov: resource.pov
        }

        // Compares w/ current resource key to toggle on/off...
        if (this.info.key === key && !forceShow) {
          this.info.isOpened = !this.info.isOpened

        // ...vs showing at resource position
        } else {
          this.info.isOpened = true
          this.info.key = key
          this.info.content = {
            title: resource.title,
            address: resource.formattedAddress,
            mapURL: resource.mapURL,
            notes: resource.notes,
            website: resource.website
          }
        }

        // If we're not forceShowing, then also scroll to the resource listing
        if (!forceShow) {
          this.$bus.$emit('focusListing', resource)
        }
      },

      toggleStreetView (position) {
        let pano = this.$refs.map.$mapObject.getStreetView()
        pano.setPano(position.pano)
        pano.setPov(position.pov)
        pano.setVisible(true)
      },

      centerMap (resource) {
        let pano = this.$refs.map.$mapObject.getStreetView()
        pano.setVisible(false)

        this.showInfo(resource, resource.placeID, true)
        this.$refs.map.$mapObject.setCenter({
          lat: resource.lat,
          lng: resource.lng
        })
      },

      /**
       * Updates the querystring with the maps position for persistence
       */
      rememberMapPosition () {
        const $map = this.$refs.map.$mapObject
        let query = qs.parse(location.search, {ignoreQueryPrefix: true}) || {}

        query.zoom = $map.getZoom()
        query.lat = $map.getCenter().lat()
        query.lng = $map.getCenter().lng()

        this.$router.replace({query})
      }
    }
  }
</script>





<style lang="sass">
  #main-map
    height: 100%

  .vue-map-container
    width: 100%
    height: 100%

  .gm-style
    p
      margin-bottom: 5px
    strong
      font-weight: 900
    pre
      margin-bottom: 0
      font-family: Roboto, Arial, sans-serif

  .map-wrap
    height: 100%
    width: 100%
    position: relative

  .overlay-wrap
    height: 100%
    width: 100%
    position: absolute
    display: table
    z-index: 100
    padding: 20px
    background: rgba(0,0,0,0.5)
    pointer-events: none

  .overlay
    height: 100%
    display: table-cell
    vertical-align: middle
    color: #fff
    text-align: center
    text-shadow: 0 0 3px rgba(0,0,0,0.35)
    pointer-events: none
</style>



// WEBPACK FOOTER //
// src/components/map/Main.vue